import { Theme as AugmentedTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { Box, Dialog, DialogContent, IconButton, Progress, Typography } from 'web-library';
import { MutationStatus } from 'react-query';
import DialogTitle from '@mui/material/DialogTitle';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ViewSubjectDetailResponse } from '../api/candidate/exam/model';
import ErrorModal from './modals/ErrorModal';

export interface ViewSubjectDetailsProps {
  modelOpen: boolean;
  subjectDetails?: ViewSubjectDetailResponse;
  onClose: (value?: string) => void;
  fetchSubjectDetailStatus: MutationStatus;
}

const useStyles = makeStyles()((theme) =>
({
  viewDetailsDialog: {
    '& .MuiPaper-root.MuiDialog-paper': {
      padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
      '& .MuiDialogTitle-root': {
        padding: 0,
      },
      '& .MuiDialogContent-root': {
        padding: 0,
      },
      '& .MuiDialogActions-root': {
        padding: 0,
      },
    },
  },
  textContainer: {
    color: '#96A1AB',
    width: '200px',
    marginRight: theme.spacing(4),
  },
  mandatoryContainer: {
    display: 'flex',
    alignSelf: 'flex-start',
    marginBottom: theme.spacing(3),
  },
  optionalPaperContainer: {
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  rightContainer: {
    width: 'calc(100% - 180px);',
  },
  progressContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  closeButton: {
    padding: 0,
  },
}));

const ViewSubjectDetailsModal = ({
  modelOpen,
  subjectDetails,
  onClose,
  fetchSubjectDetailStatus,
}: ViewSubjectDetailsProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const handleClose = () => {
    onClose();
  };
  return (
    (<Dialog
      className={classes.viewDetailsDialog}
      onClose={handleClose}
      open={modelOpen}
      maxWidth="sm"
    >
      {fetchSubjectDetailStatus === 'success' && subjectDetails && (
        <>
          <Box className={classes.titleContainer}>
            <DialogTitle>
              <Typography variant="h4">{`${subjectDetails.subjectName} (${subjectDetails.subjectCode})`}</Typography>
            </DialogTitle>
            <IconButton
              className={classes.closeButton}
              id="button-view-detail-close"
              name={IconName.cross}
              iconSize="S"
              color={theme.color.icon.iconDefault}
              ariaLabel="view-subject-detail-close"
              onClick={handleClose} />
          </Box>

          <DialogContent>
            <Box className={classes.mandatoryContainer}>
              <Typography variant="h5" className={classes.textContainer}>
                {t('components->>ViewSubjectDetails.MandatoryPapers')}
              </Typography>
              <Box className={classes.rightContainer}>
                {subjectDetails.mandatoryPapers.map((paper) => (
                  <Typography variant="body1" key={paper.modeOfAssessment}>
                    {`${t('components->>ViewSubjectDetails.Paper')} ${paper.paperNo}: ${paper.modeOfAssessment
                      }`}
                  </Typography>
                ))}
              </Box>
            </Box>
            {subjectDetails.optionalPapers.length > 0 && (
              <Box className={classes.optionalPaperContainer}>
                <Typography variant="h5" className={classes.textContainer}>
                  {t('components->>ViewSubjectDetails.OptionalPapers')}
                </Typography>
                <Box className={classes.rightContainer}>
                  {subjectDetails.optionalPapers.map((optional) => (
                    <Typography variant="body1" key={optional.modeOfAssessment}>{`${t(
                      'components->>ViewSubjectDetails.Paper'
                    )} ${optional.paperNo}: ${optional.modeOfAssessment}`}</Typography>
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
        </>
      )}
      {fetchSubjectDetailStatus === 'loading' && (
        <div className={classes.progressContainer}>
          <Progress color="secondary" />
        </div>
      )}
      {fetchSubjectDetailStatus === 'error' && <ErrorModal />}
    </Dialog>)
  );
};

export default ViewSubjectDetailsModal;
